import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable } from '@angular/core';
import { Page } from 'conversai-components';
import { BehaviorSubject, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Identifiable } from '../../core/models/identifiable.type';

export class Paging {
    static readonly FIRST_PAGE: number = 1;
}

/**
 * Define um filtro que deve ser aplicado em uma consulta.
 *
 */
export class Filter {
    name: string;
    value: string;

    constructor(name: string, value: string) {
        this.name = name;
        this.value = value;
    }

    /**
     * Obtém o filtro em um formato de QueryStrig.
     *
     * @returns Filtro no formato de QueryString.
     */
    toQueryString(): string {
        return `${this.name}=${this.value}`;
    }
}

/**
 * Define um conjunto de filtros que podem ser aplicados em uma consulta.
 *
 */
export class Filters {
    private readonly filters: Filter[] = [];

    constructor(filters: Filter[] = []) {
        this.filters.push(...filters);
    }

    /**
     * Adiciona um filtro ao conjunto de filtros.
     *
     * @param name Nome do filtro.
     * @param value Valor do filtro.
     * @returns void
     */
    add(name: string, value: string): void {
        // Verifica se o filtro já existe e atualiza o valor, caso exista.
        const existingFilter = this.filters.find(
            (filter) => filter.name === name,
        );

        if (existingFilter) {
            existingFilter.value = value;
            return;
        }

        this.filters.push(new Filter(name, value));
    }

    /**
     * Obtém a quantidade de filtros aplicados.
     *
     * @return Número de filtros aplicados.
     */
    size(): number {
        return this.filters.length;
    }

    /**
     * Obtém os filtros em um formato de QueryString.
     *
     * @returns String representando os filtros no formato de QueryString.
     */
    toQueryString(): string {
        return this.filters.map((filter) => filter.toQueryString()).join('&');
    }
}

export class PagedSearch {
    currentPage: number;
    perPage: number;
    filters: Filters;

    constructor(
        currentPage: number,
        perPage: number,
        filters: Filters | null = null,
    ) {
        this.currentPage = currentPage > 0 ? currentPage : 1;
        this.perPage = perPage > 0 ? perPage : 10;
        this.filters = filters || new Filters();
    }

    toQueryString(): string {
        const params: Record<string, string> = {};

        if (this.currentPage > 0) {
            params['current_page'] = String(this.currentPage);
        }

        if (this.perPage > 0) {
            params['per_page'] = String(this.perPage);
        }

        if (this.filters?.size() > 0) {
            const filterString = this.filters.toQueryString(); // ex: "status=active&role=admin"
            const baseQuery = new URLSearchParams(params).toString();
            return `?${baseQuery}&${filterString}`;
        }

        const query = new URLSearchParams(params).toString();
        return query ? `?${query}` : '';
    }
}

export class SortedField {
    constructor(
        public field: string,
        public direction: 'asc' | 'desc' = 'asc',
    ) {}

    toQueryString(): string {
        return `sort=${this.field},${this.direction}`;
    }
}

export class Sorting {
    public static readonly ASC = 'asc';
    public static readonly DESC = 'desc';
    private readonly fields: SortedField[] = [];

    constructor(fields: SortedField[] = []) {
        this.fields.push(...fields);
    }

    addField(field: SortedField): void {
        if (!field || !field.field) {
            throw new Error(
                'Field and direction must be provided for the sorted field.',
            );
        }

        // Verifica se o campo já existe e atualiza a direção, caso exista.
        const existingField = this.fields.find((f) => f.field === field.field);

        if (existingField) {
            existingField.direction = field.direction;
            return;
        }

        this.fields.push(field);
    }

    toQueryString(): string {
        return this.fields.map((field) => field.toQueryString()).join('&');
    }
}

/**
 * Define uma consulta paginada.
 * Simplifica a realização de consultas a APIs que usam o padrão
 * de consulta:
 *
 *    /endpoint?filter1=teste&filter2=teste2&sort=name,asc&per_page=10&current_page=1
 *
 * As consultas ao Endpoint são criadas dinamicamente, usando o padrão acima.
 *
 * @param T Tipo dos dados que serão retornados na consulta.
 */
export class PagedQuery<T extends Identifiable> {
    private sorting: Sorting;
    private currentPage: number = 1;
    private pageSize: number = 5;
    private total: number = 0;
    private filters: Filters = new Filters();
    private readonly dataSubject = new BehaviorSubject<Page<T>>({
        data: [],
        currentPage: this.currentPage,
        total: 0,
        pageSize: this.pageSize,
    } as Page<T>);
    private readonly dataObservable = this.dataSubject.asObservable();
    private readonly endpoint: string;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly destroyRef: DestroyRef,
        readonly baseUrl: string,
        pageSize: number,
        readonly path: string,
        readonly preFetchFirstPage: boolean = false,
        sorting: Sorting = new Sorting(),
        filters: Filters = new Filters(),
    ) {
        this.endpoint = `${baseUrl}/${path}`;
        this.pageSize = pageSize;
        this.sorting = sorting;
        this.filters = filters;
        this.baseUrl = baseUrl;

        if (preFetchFirstPage) {
            this.fetch();
        }
    }

    /**
     * Obtém um Observable que emite a página atual quando ela estiver pronta.
     *
     * @returns Observable que emite a página atual quando ela estiver pronta.
     */
    onPageReady(): Observable<Page<T>> {
        return this.dataObservable;
    }

    /**
     * Obtém a página atual.
     *
     * @returns A página atual.
     */
    getNumberOfPages(): number {
        if (this.total <= 0) {
            return 0;
        }

        return Math.ceil(this.total / this.pageSize);
    }

    /**
     * Adiciona um campo de ordenação à consulta.
     *
     * @param field O campo a ser ordenado.
     * @param direction A direção da ordenação (asc ou desc).
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    sortBy(
        field: string,
        direction: 'asc' | 'desc' | '' | undefined,
    ): PagedQuery<T> {
        this.sorting.addField(
            new SortedField(
                field,
                direction === 'asc' ? Sorting.ASC : Sorting.DESC,
            ),
        );
        return this;
    }

    /**
     * Adiciona um filtro à consulta.
     *
     * @param name O nome do filtro.
     * @param value O valor do filtro.
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    filterBy(name: string, value: any): PagedQuery<T> {
        this.currentPage = 1;
        this.filters.add(name, value);
        return this;
    }

    /**
     * Limpa os campos de ordenação da consulta.
     *
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    clearSorting(): PagedQuery<T> {
        this.sorting = new Sorting();
        return this;
    }

    /**
     * Limpa os filtros aplicados na consulta.
     *
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    clearFilters(): PagedQuery<T> {
        this.filters = new Filters();
        return this;
    }

    /**
     * Avança para a próxima página da consulta.
     *
     * @returns void
     */
    next(): void {
        this.currentPage++;
        this.fetch();
    }

    /**
     * Retorna à página anterior da consulta.
     *
     * @returns void
     */
    previous(): void {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.fetch();
        }
    }

    /**
     * Vai para a página especificada da consulta.
     *
     * @param page Número da página para a qual ir.
     * @returns void
     */
    goToPage(page: number): void {
        if (page > 0) {
            this.currentPage = page;
            this.fetch();
        }
    }

    /**
     * Realiza a busca dos dados na API.
     *
     * Esta função constrói a consulta paginada com base nos filtros,
     * ordenamentos e página atual, e faz uma requisição HTTP GET
     * para o endpoint configurado.
     *
     * @returns void
     */
    fetch(): void {
        const search = new PagedSearch(
            this.currentPage,
            this.pageSize,
            this.filters,
        );
        const sortQueryString = this.sorting.toQueryString();
        this.httpClient
            .get<Page<T>>(
                `${this.endpoint}/${search.toQueryString()}${sortQueryString ? `&${sortQueryString}` : ''}`,
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (value: Page<T>) => {
                    this.total = value.total;
                    this.handleCurrentPage(value);
                    this.dataSubject.next(value);
                },
                error: (error: HttpErrorResponse) => {
                    console.error(error);
                    this.dataSubject.error(error);
                },
            });
    }

    /**
     * Obtém todos os registros contidos em uma única página.
     *
     */
    all(): void {
        this.currentPage = -1;
        this.pageSize = -1;
        this.fetch();
    }

    /**
     * Trata a página atual recebida da API.
     * Atualiza a página atual se a página recebida contiver dados.
     *
     * @param page A página recebida da API.
     * @returns void
     */
    private handleCurrentPage(page: Page<T>): void {
        if (page && page.data && page.data.length > 0) {
            this.currentPage = page.currentPage;
        }
    }
}

/**
 * Define uma consulta simples, sem paginação.
 * Simplifica a realização de consultas a APIs que usam o padrão de consulta:
 *
 *    /endpoint?filter1=teste&filter2=teste2&sort=name,asc
 *
 * As consultas ao Endpoint são criadas dinamicamente, usando o padrão acima.
 *
 * @param T Tipo dos dados que serão retornados na consulta.
 */
export class ListQuery<T extends Identifiable> {
    private sorting: Sorting;
    private total: number = 0;
    private filters: Filters = new Filters();
    private readonly dataSubject = new BehaviorSubject<T[]>([]);
    private readonly dataObservable = this.dataSubject.asObservable();
    private readonly endpoint: string;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly destroyRef: DestroyRef,
        readonly baseUrl: string,
        readonly path: string,
        readonly preFetch: boolean = false,
        sorting: Sorting = new Sorting(),
        filters: Filters = new Filters(),
    ) {
        this.endpoint = `${baseUrl}/${path}`;
        this.sorting = sorting;
        this.filters = filters;
        this.baseUrl = baseUrl;

        if (preFetch) {
            this.fetch();
        }
    }

    /**
     * Obtém um Observable que emite a página atual quando ela estiver pronta.
     *
     * @returns Observable que emite a página atual quando ela estiver pronta.
     */
    onReady(): Observable<T[]> {
        return this.dataObservable;
    }

    /**
     * Adiciona um campo de ordenação à consulta.
     *
     * @param field O campo a ser ordenado.
     * @param direction A direção da ordenação (asc ou desc).
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    sortBy(
        field: string,
        direction: 'asc' | 'desc' | '' | undefined,
    ): ListQuery<T> {
        this.sorting.addField(
            new SortedField(
                field,
                direction === 'asc' ? Sorting.ASC : Sorting.DESC,
            ),
        );
        return this;
    }

    /**
     * Adiciona um filtro à consulta.
     *
     * @param name O nome do filtro.
     * @param value O valor do filtro.
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    filterBy(name: string, value: any): ListQuery<T> {
        this.filters.add(name, value);
        return this;
    }

    /**
     * Limpa os campos de ordenação da consulta.
     *
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    clearSorting(): ListQuery<T> {
        this.sorting = new Sorting();
        return this;
    }

    /**
     * Limpa os filtros aplicados na consulta.
     *
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    clearFilters(): ListQuery<T> {
        this.filters = new Filters();
        return this;
    }

    /**
     * Realiza a busca dos dados na API.
     *
     * Esta função constrói a consulta paginada com base nos filtros,
     * ordenamentos e página atual, e faz uma requisição HTTP GET
     * para o endpoint configurado.
     *
     * @returns void
     */
    fetch(): void {
        const filterQueryString = this.filters.toQueryString();
        const sortQueryString = this.sorting.toQueryString();
        this.httpClient
            .get<T[]>(
                `${this.endpoint}/?${filterQueryString}${sortQueryString ? `&${sortQueryString}` : ''}`,
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (value: T[]) => {
                    this.total = value.length;
                    this.dataSubject.next(value);
                },
                error: (error: HttpErrorResponse) => {
                    console.error(error);
                    this.dataSubject.error(error);
                },
            });
    }
}

/**
 * Builder para simplificar a criação de objetos PagedQuery.
 *
 * Ao ser injetado em um componente, já leva junto o httpClient e destroyRef
 * necessários para o funcionamento do PagedQuery criada.
 */
@Injectable({ providedIn: 'root' })
export class PagedQueryBuilder {
    private aBaseUrl: string | null = null;
    private aPath: string | null = null;
    private aPageSize: number | null = null;
    private aPreFetchFirstPage: boolean = false;
    private aSortedFields: SortedField[] = [];
    private aFilters: Filter[] = [];

    constructor(
        private readonly httpClient: HttpClient,
        private readonly destroyRef: DestroyRef,
    ) {}

    /**
     * Define o caminho da API para a consulta paginada.
     *
     * @param path Caminho da API.
     * @return Instância do PagedQueryBuilder para encadeamento de chamadas.
     */
    path(path: string): PagedQueryBuilder {
        this.aPath = path;
        return this;
    }

    /**
     * Define a URL base para a consulta paginada.
     *
     * @param baseUrl URL base da API.
     * @return Instância do PagedQueryBuilder para encadeamento de chamadas.
     */
    baseUrl(baseUrl: string): PagedQueryBuilder {
        this.aBaseUrl = baseUrl;
        return this;
    }

    /**
     * Define o tamanho da página para a consulta paginada.
     *
     * @param pageSize Tamanho da página.
     * @return Instância do PagedQueryBuilder para encadeamento de chamadas.
     */
    pageSize(pageSize: number): PagedQueryBuilder {
        this.aPageSize = pageSize;
        return this;
    }

    /**
     * Define se a primeira página deve ser pré-carregada.
     *
     * @param preFetchFirstPage Indica se a primeira página deve ser pré-carregada.
     * @return Instância do PagedQueryBuilder para encadeamento de chamadas.
     */
    preFetchFirstPage(preFetchFirstPage: boolean): PagedQueryBuilder {
        this.aPreFetchFirstPage = preFetchFirstPage;
        return this;
    }

    /**
     * Adiciona um campo para ordenação na consulta paginada.
     *
     * @param field Campo a ser ordenado.
     * @param direction Direção da ordenação (asc ou desc).
     * @return Instância do PagedQueryBuilder para encadeamento de chamadas.
     */
    sortBy(field: string, direction: 'asc' | 'desc'): PagedQueryBuilder {
        this.aSortedFields.push(new SortedField(field, direction));
        return this;
    }

    /**
     * Adiciona um filtro à consulta paginada.
     *
     * @param name Nome do filtro.
     * @param value Valor do filtro.
     * @return Instância do PagedQueryBuilder para encadeamento de chamadas.
     */
    filterBy(name: string, value: string): PagedQueryBuilder {
        this.aFilters.push(new Filter(name, value));
        return this;
    }

    /**
     * Constrói e retorna uma instância de PagedQuery com as configurações definidas.
     *
     * @returns Instância de PagedQuery configurada.
     */
    build<T extends Identifiable>(): PagedQuery<T> {
        if (!this.aPath || !this.aPageSize || !this.aBaseUrl) {
            throw new Error(
                'Os parâmetros BaseUrl, Path e PageSize são obrigatórios.',
            );
        }

        const pagedQuery = new PagedQuery<T>(
            this.httpClient,
            this.destroyRef,
            this.aBaseUrl,
            this.aPageSize,
            this.aPath,
            this.aPreFetchFirstPage,
            new Sorting(this.aSortedFields),
            new Filters(this.aFilters),
        );

        return pagedQuery;
    }
}

/**
 * Builder para simplificar a criação de objetos ListQuery.
 *
 * Ao ser injetado em um componente, já leva junto o httpClient e destroyRef
 * necessários para o funcionamento do ListQuery criada.
 */
@Injectable({ providedIn: 'root' })
export class ListQueryBuilder {
    private aBaseUrl: string | null = null;
    private aPath: string | null = null;
    private aPreFetch: boolean = false;
    private aSortedFields: SortedField[] = [];
    private aFilters: Filter[] = [];

    constructor(
        private readonly httpClient: HttpClient,
        private readonly destroyRef: DestroyRef,
    ) {}

    /**
     * Define o caminho da API para a consulta paginada.
     *
     * @param path Caminho da API.
     * @return Instância do ListQueryBuilder para encadeamento de chamadas.
     */
    path(path: string): ListQueryBuilder {
        this.aPath = path;
        return this;
    }

    /**
     * Define a URL base para a consulta paginada.
     *
     * @param baseUrl URL base da API.
     * @return Instância do ListQueryBuilder para encadeamento de chamadas.
     */
    baseUrl(baseUrl: string): ListQueryBuilder {
        this.aBaseUrl = baseUrl;
        return this;
    }

    /**
     * Define se a primeira página deve ser pré-carregada.
     *
     * @param preFetch Indica se a primeira página deve ser pré-carregada.
     * @return Instância do ListQueryBuilder para encadeamento de chamadas.
     */
    preFetch(preFetch: boolean): ListQueryBuilder {
        this.aPreFetch = preFetch;
        return this;
    }

    /**
     * Adiciona um campo para ordenação na consulta paginada.
     *
     * @param field Campo a ser ordenado.
     * @param direction Direção da ordenação (asc ou desc).
     * @return Instância do ListQueryBuilder para encadeamento de chamadas.
     */
    sortBy(field: string, direction: 'asc' | 'desc'): ListQueryBuilder {
        this.aSortedFields.push(new SortedField(field, direction));
        return this;
    }

    /**
     * Adiciona um filtro à consulta paginada.
     *
     * @param name Nome do filtro.
     * @param value Valor do filtro.
     * @return Instância do ListQueryBuilder para encadeamento de chamadas.
     */
    filterBy(name: string, value: string): ListQueryBuilder {
        this.aFilters.push(new Filter(name, value));
        return this;
    }

    /**
     * Constrói e retorna uma instância de PagedQuery com as configurações definidas.
     *
     * @returns Instância de PagedQuery configurada.
     */
    build<T extends Identifiable>(): ListQuery<T> {
        if (!this.aPath || !this.aBaseUrl) {
            throw new Error('Os parâmetros BaseUrl e Path são obrigatórios.');
        }

        const listQuery = new ListQuery<T>(
            this.httpClient,
            this.destroyRef,
            this.aBaseUrl,
            this.aPath,
            this.aPreFetch,
            new Sorting(this.aSortedFields),
            new Filters(this.aFilters),
        );

        return listQuery;
    }
}
